import type { ContactSaveResponse } from "@/modules/common/utils/contact-merge";
import { brandUrl, fetchFromBrandedUrl } from "@/api/utils";
import {
  BasePaginationRequest,
  EnvironmentVariables,
  FilterDto,
  RanksResponse,
  SubDepartmentsDto,
} from "@/modules/common/types";
import { UUID } from "crypto";
import { AxiosRequestConfigMethods } from "@/api/types";
import {
  Customer,
  Journey,
  Lead,
  MethodOfContact,
  MethodsOfContactFilters,
  StatusCategory,
  UpdateCustomerRequest,
  UpdateLeadRequest,
  UpdateEmails,
  UpdateTelephone,
  AddTelephone,
  UpdateJourneyRequest,
  UpdateJourneyStatusRequest,
  FormFieldsResponse,
} from "@/modules/common/dtos/index.v2";
import { AxiosResponse } from "axios";
import {
  GetLeadTypeResponseV2,
  GetViewerListResponse,
  MergeCustomerDtoRequest,
  LeadHistoryItem,
  UpdateLeadTypeRequestV2,
  AddReferralRequest,
  ActivityType,
  ActivityResponseDto,
  NawyNowRecommendationsResponseDto,
  GetLeadRatingsFiltersDto,
  ResolveRatingDto,
  WhatsappConversationResponse,
  WhatsappMessage,
  WhatsappAgentGroup,
  ZoomCallDetailResponse,
} from "@/modules/lead-page/dtos/index-v2";
import { JourneysResponse } from "@/modules/leads/dtos/index.v2";
import { LeadTag } from "@/modules/lead-tags/dtos";
import {
  AddInterestActionInput,
  AddInterestInput,
  CursorInterestActionsResponse,
  InterestProperty,
  InterestAction,
  InterestPropertyResponse,
  JourneyRelationType,
  LeadReferralResponse,
  PropertiesForInterestsResponse,
  QueryInterestActionsCursorInput,
} from "@/modules/lead-page/types";
import { FunnelStageResponse } from "@/modules/lead-page/types";
import { AddRatingCommentDto } from "@/modules/lead-page/dtos/index-v2";
import { ListingProperty } from "@/modules/listing-dashboard/dtos/index-v2";

export const editCustomer = async (
  customerId: UUID | undefined,
  data: UpdateCustomerRequest,
): Promise<Customer> => {
  if (!customerId) {
    throw new Error("Customer ID is required");
  }

  const apiUrl = brandUrl<Customer>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/customers/${customerId}`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
      data,
    },
  });
};

export const editJourney = async (
  journeyUId: UUID | undefined,
  data: UpdateJourneyRequest,
): Promise<Customer> => {
  if (!journeyUId) {
    throw new Error("Customer ID is required");
  }

  const apiUrl = brandUrl<Customer>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/journeys/${journeyUId}`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
      data,
    },
  });
};

export const editJourneyStatus = async (
  journeyUId: UUID | undefined,
  data: UpdateJourneyStatusRequest,
) => {
  const apiUrl = brandUrl<JourneysResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/journeys/${journeyUId}/update-status`,
  );

  const formData = new FormData();
  formData.append("statusUId", data.statusUId);
  if (data.comment) formData.append("comment", data.comment);
  if (data.trackUId) formData.append("trackUId", data.trackUId);
  if (data.propertyTrackUIds?.length)
    formData.append("propertyTrackUIds", JSON.stringify(data.propertyTrackUIds));
  if (data.formData) formData.append("formData", JSON.stringify(data.formData));
  if (data.erealtyForm) {
    formData.append("erealtyForm", JSON.stringify(data.erealtyForm));
  }
  if (data.erealtyFormData) {
    formData.append("erealtyFormData", JSON.stringify(data.erealtyFormData));
  }

  if (data.attachments && data.attachments.length > 0) {
    data.attachments.forEach((file) => formData.append("attachments", file));
  }
  if (data.attachmentMetadata && data.attachmentMetadata.length > 0) {
    formData.append(
      "attachmentMetadata",
      JSON.stringify(data.attachmentMetadata),
    );
  }

  if (data.salesForm) {
    formData.append("salesForm", JSON.stringify(data.salesForm));
    if (data.saleDTO) formData.append("saleDTO", JSON.stringify(data.saleDTO));

    data.saleFiles?.forEach((file) => formData.append("files", file));
    data.salePaymentFiles?.forEach((file) =>
      formData.append("paymentFiles", file),
    );
    data.salePaymentPlanFiles?.forEach((file) =>
      formData.append("paymentPlanFiles", file),
    );
  }

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      formData: true,
      data: formData,
    },
  });
};

export const updateExpoStatus = async (
  uid: UUID | undefined,
  data: UpdateJourneyStatusRequest,
) => {
  const apiUrl = brandUrl<JourneysResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/journeys/${uid}/update-expo-status`,
  );

  const formData = new FormData();
  formData.append("statusUId", data.statusUId);

  if (data.comment) {
    formData.append("comment", data.comment);
  }

  if (data.formData) {
    formData.append("formData", JSON.stringify(data.formData));
  }

  if (data.attachments && data.attachments.length > 0) {
    data.attachments.forEach((file) => {
      formData.append("attachments", file);
    });
  }
  if (data.attachmentMetadata && data.attachmentMetadata.length > 0) {
    formData.append(
      "attachmentMetadata",
      JSON.stringify(data.attachmentMetadata),
    );
  }

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      formData: true,
      data: formData,
    },
  });
};

export const getFormFields = (formUId: string) => {
  const apiUrl = brandUrl<FormFieldsResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/forms/${formUId}`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: "GET",
    },
  }) as Promise<{ data: FormFieldsResponse }>;
};

export const addCustomerEmail = async (
  customerId: UUID | undefined,
  data: UpdateEmails,
): Promise<Customer> => {
  const apiUrl = brandUrl<Customer>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/customers/${customerId}/customer-emails`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data,
    },
  });
};

export const addCustomerTelephone = async (
  customerId: UUID | undefined,
  data: AddTelephone,
): Promise<ContactSaveResponse> => {
  if (!customerId) {
    throw new Error("Customer ID is required");
  }

  const apiUrl = brandUrl<AxiosResponse<ContactSaveResponse>>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/customers/${customerId}/customer-telephones`,
  );

  const response = await fetchFromBrandedUrl<AxiosResponse<ContactSaveResponse>>({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data,
    },
  });
  return response.data;
};

export const updateCustomerTelephone = async (
  telephoneID: UUID | undefined,
  data: UpdateTelephone,
): Promise<Customer> => {
  const apiUrl = brandUrl<Customer>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/customer-telephones/${telephoneID}`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
      data,
    },
  });
};

export const deleteCustomerTelephone = async (
  telephoneID: UUID | undefined,
): Promise<Customer> => {
  const apiUrl = brandUrl<Customer>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/customer-telephones/${telephoneID}`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.DELETE,
    },
  });
};
export const getLead = async ({
  id,
  signal,
}: {
  id: UUID;
  signal?: AbortSignal;
}): Promise<AxiosResponse<Lead>> => {
  const apiUrl = brandUrl<Lead>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/leads/${id}`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      signal,
    },
  });
};

export const editLead = async (
  lead_id: UUID | undefined,
  data: UpdateLeadRequest,
): Promise<Lead> => {
  if (!lead_id) {
    throw new Error("Lead ID is required");
  }

  const apiUrl = brandUrl<Lead>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/leads/${lead_id}`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
      data,
    },
  });
};

export const getJourney = async ({
  id,
  signal,
  mode,
  hasQRCode,
}: {
  id: string;
  signal?: AbortSignal;
  mode?: JourneyRelationType;
  hasQRCode?: boolean;
}): Promise<AxiosResponse<Journey>> => {
  const apiUrl = brandUrl<Journey>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/journeys/${id}/find-by-nano-id`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...(mode ? { mode } : {}),
        ...(hasQRCode ? { hasQRCode } : {}),
      },
      signal,
    },
  });
};

export const getJourneyByOldId = async ({
  id,
  signal,
}: {
  id: string | undefined;
  signal?: AbortSignal;
}): Promise<AxiosResponse<Journey>> => {
  const apiUrl = brandUrl<Journey>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/journeys/${id}/find-by-old-id`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      signal,
    },
  });
};

export const getMethodsOfContact = async ({
  params,
  signal,
}: {
  params: MethodsOfContactFilters;
  signal?: AbortSignal;
}): Promise<AxiosResponse<MethodOfContact>> => {
  const apiUrl = brandUrl<MethodOfContact>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/methods-of-contact`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params,
      signal,
    },
  });
};

export const getStatusCategory = async ({
  id,
  signal,
}: {
  id: UUID;
  signal?: AbortSignal;
}): Promise<AxiosResponse<StatusCategory>> => {
  const apiUrl = brandUrl<StatusCategory>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/status-categories/${id}`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      signal,
    },
  });
};

export const getLeadViewerV1 = async (lead_id: number | string | undefined) => {
  const apiUrl = brandUrl<GetViewerListResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/${lead_id}/journey-viewers`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  });
};

export const addLeadViewerV1 = async (
  lead_id: number | string | undefined,
  userUId: number | string | undefined,
) => {
  const apiUrl = brandUrl<GetViewerListResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/${lead_id}/journey-viewers`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: { userUId: userUId },
    },
  });
};

export const deleteLeadViewerV1 = async (UID: number | string | undefined) => {
  const apiUrl = brandUrl<GetViewerListResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/journey-viewers/${UID}`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.DELETE,
    },
  });
};
//TODO add return type
export const getLeadTypes = async (
  params: {
    page: number;
    pageSize: number;
  },
  organizationUId?: string,
) => {
  const apiUrl = brandUrl<GetLeadTypeResponseV2>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/lead-types`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...params,
        ...(organizationUId && { organizationUId }),
      },
      ...(!organizationUId && { includeSingleOrganization: true }),
    },
  });
};

export const mergeLeadsV2 = async (
  body: MergeCustomerDtoRequest,
  parentLeadId: string,
) => {
  const apiUrl = brandUrl<JourneysResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/customers/${parentLeadId}/merge-customers`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: body,
    },
  });
};

export const sendPersonalMeetingRoomV2 = async (
  lead_id: number | string | UUID,
  meeting_timing: string,
) => {
  const apiUrl = brandUrl<JourneysResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/${lead_id}/zoom-meetings`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: {
        meetingTiming: meeting_timing,
      },
    },
  });
};

export const getLeadListingsV2 = async (
  journeyUId: string,
  includePropertiesWithoutListings?: boolean,
  pagination?: {
    page: number;
    pageSize: number;
  },
) => {
  const apiUrl = brandUrl<ListingProperty>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/listings`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: "GET",
      params: {
        journeyUId,
        includePropertiesWithoutListings,
        ...pagination,
      },
    },
  });
};

export const updateLeadTypeV2 = async (
  lead_id: number | string,
  data: UpdateLeadTypeRequestV2,
) => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/leads/${lead_id}/update-lead-type`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data,
    },
  });
};

export const assignLeadV2 = ({
  journeyUId,
  userUId,
}: {
  journeyUId: string | undefined;
  userUId: string;
}) => {
  const apiUrl = brandUrl<JourneysResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/journeys/${journeyUId}/assign`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: { userUId: userUId },
    },
  });
};

export const getLeadHistory = async (params: any) => {
  const apiUrl = brandUrl<{ data: LeadHistoryItem[] }>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/audits/lead-history`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params,
    },
  }) as Promise<{ data: LeadHistoryItem[] }>;
};

export const autoAssignLeadV2 = async (journeyUId: UUID) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/journeys/${journeyUId}/auto-assign`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
    },
  });
};

export const markLeadViewerAsReferrerV2 = async (
  leadUId: string | undefined,
  referrerUId: string | undefined,
) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/${leadUId}/lead-referrals`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: { referrerUId: referrerUId },
    },
  });
};

export const removeLeadViewerAsReferrerV2 = async (
  referrerUId: string | undefined,
) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/${referrerUId}`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.DELETE,
    },
  });
};

export const fetchDataForFormFactory = (path: string, params: any) => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.NEW_DYNAMIC_CRM_BASE_API_URL}${path}`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params,
    },
  });
};

export const getMentionList = (journeyUId: string | undefined) => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/comments/mentionable-users/${journeyUId}`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  });
};

export const getRanks = async (params: {
  page: number;
  pageSize: number;
  title?: string;
}) => {
  const apiUrl = brandUrl<RanksResponse>(
    `${EnvironmentVariables.UMS_API_URL}/ranks`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params,
    },
  }) as Promise<RanksResponse>;
};

export const getSubDepartments = async (params: {
  page: number;
  pageSize: number;
  name?: string;
  organizationUId?: string;
  hasTarget?: boolean;
}) => {
  const apiUrl = brandUrl<SubDepartmentsDto>(
    `${EnvironmentVariables.UMS_API_URL}/sub-departments`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params,
      ...(!params.organizationUId && {
        includeSingleOrganization: true,
      }),
    },
  }) as Promise<SubDepartmentsDto>;
};

export const deleteJourney = async (journeyUId: string | undefined) => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/journeys/${journeyUId}`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.DELETE,
    },
  });
};

export const startCallV2 = (
  id: number | string,
  zoomCall?: boolean | undefined,
) => {
  const path = zoomCall ? "start-zoom-call" : "start-call";
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/comments/${id}/${path}`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
    },
  });
};

export const deleteLeadTag = async (leadUId: string, leadTagUId: string) => {
  const apiUrl = brandUrl<LeadTag>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/leads/${leadUId}/remove-lead-tag`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
      data: {
        removeLeadTagUId: leadTagUId,
      },
    },
  });
};

export const addLeadTag = async (leadUId: string, tagUIds: string[]) => {
  const apiUrl = brandUrl<LeadTag>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/leads/${leadUId}/add-lead-tag`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
      data: {
        tagUIds: tagUIds,
      },
    },
  });
};

export const getLeadReferrals = async (leadUId: string) => {
  const apiUrl = brandUrl<LeadReferralResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/${leadUId}/lead-referrals`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: "GET",
    },
  }) as Promise<LeadReferralResponse>;
};

export const updateLeadReferral = async (
  referralUId: string,
  validityExpirationDate: string,
) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/${referralUId}/lead-referrals`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
      data: {
        validityExpirationDate: validityExpirationDate,
      },
    },
  });
};

export const getAssignmentReport = async (assignmentUid: string) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/journey-user-updates/${assignmentUid}/analyze-assignment`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  });
};

export const addLeadReferral = async (
  leadUId: string,
  data: AddReferralRequest,
) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/${leadUId}/lead-referrals`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data,
    },
  });
};

export const deleteLeadReferral = async (referralUId: string) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/${referralUId}/lead-referrals`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.DELETE,
    },
  });
};

export const reassignJourneyToManager = async (journeyUId: string) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/journeys/${journeyUId}/reassign-to-manager`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  });
};

export const getActivityTimeline = async (
  journeyUId: string,
  params: {
    limit: number;
    type: ActivityType[];
    includeChildLead: boolean;
    cursor?: string;
    direction?: "desc" | "asc";
    agentUids?: string[];
  },
): Promise<ActivityResponseDto> => {
  const apiUrl = brandUrl<ActivityResponseDto>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/activity/${journeyUId}`,
  );

  const res = await fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params,
    },
  });

  return res;
};

export const getNawyNowRecommendations = async (
  propertyId: number | null | undefined,
) => {
  const apiUrl = brandUrl<NawyNowRecommendationsResponseDto>(
    `https://listing-api.cooingestate.com/crm/getrecommendednawynow/${propertyId}`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      headers: {
        apiKey:
          EnvironmentVariables.LISTING_RECOMMENDATIONS_API_KEY as unknown as string,
      },
    },
  });
};

export const getStagesByJourney = async (
  journeyNanoId: string,
): Promise<FunnelStageResponse> => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/journeys/${journeyNanoId}/stages`,
  );

  return fetchFromBrandedUrl<FunnelStageResponse>({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  });
};

export const queryInterestsPerJoruney = async ({
  journeyUids,
  pagination,
}: {
  journeyUids?: string[];
  pagination: BasePaginationRequest;
}) => {
  const apiUrl = brandUrl<Promise<AxiosResponse<InterestPropertyResponse>>>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/interests`,
  );

  return fetchFromBrandedUrl<Promise<AxiosResponse<InterestPropertyResponse>>>({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        journeyUids,
        ...pagination,
      },
    },
  });
};

export const getInterestActionTypesFilterOptions = async ({
  interestTypeId,
}: {
  interestTypeId?: number;
}) => {
  const apiUrl = brandUrl<Promise<AxiosResponse<FilterDto[]>>>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/interest-action-types/filter-options`,
  );

  return fetchFromBrandedUrl<Promise<AxiosResponse<FilterDto[]>>>({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        interestTypeId,
      },
    },
  });
};

export const addInterestAction = async ({
  data,
}: {
  data: AddInterestActionInput;
}) => {
  const apiUrl = brandUrl<Promise<AxiosResponse<InterestAction>>>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/interest-actions`,
  );

  return fetchFromBrandedUrl<Promise<AxiosResponse<InterestAction>>>({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data,
    },
  });
};

export const queryInterestActionsCursor = async (
  params: QueryInterestActionsCursorInput,
) => {
  const apiUrl = brandUrl<
    Promise<AxiosResponse<CursorInterestActionsResponse>>
  >(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/interest-actions/cursor`,
  );

  return fetchFromBrandedUrl<
    Promise<AxiosResponse<CursorInterestActionsResponse>>
  >({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params,
    },
  });
};

export const getPropertiesForInterests = async ({
  leadTypeUid,
  journeyUid,
  search,
  pagination,
}: {
  leadTypeUid: string;
  journeyUid: string;
  search?: string;
  pagination: {
    page: number;
    pageSize: number;
  };
}) => {
  const apiUrl = brandUrl<Promise<PropertiesForInterestsResponse>>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/properties/query-for-interests`,
  );

  return fetchFromBrandedUrl<Promise<PropertiesForInterestsResponse>>({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: { ...pagination, leadTypeUid, search, journeyUid },
    },
  });
};

export const addInterest = async ({ data }: { data: AddInterestInput }) => {
  const apiUrl = brandUrl<Promise<AxiosResponse<InterestProperty>>>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/interests`,
  );

  return fetchFromBrandedUrl<Promise<AxiosResponse<InterestProperty>>>({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data,
    },
  });
};

export const getLeadRatings = async (filters?: GetLeadRatingsFiltersDto) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/ratings`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: filters,
    },
  });
};

export const addRatingComment = async (
  ratingUId: string,
  data: AddRatingCommentDto,
) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/ratings/${ratingUId}/comments`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data,
    },
  });
};

export const resolveRating = async (
  ratingUId: string,
  data: ResolveRatingDto,
) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/ratings/${ratingUId}/resolve`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
      data,
    },
  });
};

export const getRatingByUId = async (ratingUId: string) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/ratings/${ratingUId}`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  });
};

export const getEligibleInterestActionTypesFilterOptions = async ({
  interestUid,
}: {
  interestUid: string;
}) => {
  const apiUrl = brandUrl<Promise<AxiosResponse<FilterDto[]>>>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/interests/${interestUid}/eligible-actions-filter-options`,
  );

  return fetchFromBrandedUrl<Promise<AxiosResponse<FilterDto[]>>>({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  });
};

export const previewQrCode = async (
  journeyUId: string,
  forceRegenerate?: boolean,
) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/expo/preview-qr`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: forceRegenerate ? { journeyUId, forceRegenerate } : { journeyUId },
    },
  });
};

export const sendQrCode = async (journeyUId: string, qrCodeUrl: string) => {
  const apiUrl = brandUrl<{ success: boolean; message: string }>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/expo/send-qr`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: { journeyUId, qrCodeUrl },
    },
  });
};

export const getJourneyAccessLink = async (journeyUId: string) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/expo/get-access-link`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: { journeyUId },
    },
  });
};

export const getJourneyUnauthorizedAccessDetails = async (
  journeyUId: string,
  statusUId: string,
) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/journeys/${journeyUId}/status/${statusUId}/unauthorized-access`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: { journeyUId, statusUId },
    },
  });
};

export type WhatsappConversationPage = {
  messages: WhatsappConversationResponse;
  nextCursor: string | null;
};

export const getWhatsappConversation = async (
  conversationUid: string,
  cursor?: string,
): Promise<WhatsappConversationPage> => {
  const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
  const apiUrl = brandUrl<WhatsappConversationPage>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/whatsapp-conversations/${conversationUid}/messages${qs}`,
  );
  const response = await fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  });
  return (response as any).data as WhatsappConversationPage;
};

export const getZoomCallDetail = async (
  zoomCallUid: string,
): Promise<ZoomCallDetailResponse> => {
  const apiUrl = brandUrl<ZoomCallDetailResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/zoom-calls/${zoomCallUid}`,
  );
  const response = await fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  });
  return (response as any).data;
};

/** Marks that the agent created task-manager tasks from this call's outcome. */
export const markZoomCallTasksCreated = async (
  zoomCallUid: string,
): Promise<{ tasksCreatedAt: string }> => {
  const apiUrl = brandUrl<{ tasksCreatedAt: string }>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/zoom-calls/${zoomCallUid}/tasks-created`,
  );
  const response = await fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
    },
  });
  return (response as any).data;
};

export const getZoomCallTranscriptionUrl = async (
  zoomCallUid: string,
): Promise<{ url: string }> => {
  const apiUrl = brandUrl<{ url: string }>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/zoom-calls/${zoomCallUid}/transcription`,
  );

  const response = await fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  });
  return (response as any).data;
};

export interface WhatsappAgentGroupPage {
  page: number;
  pageSize: number;
  total: number;
  results: WhatsappAgentGroup[];
}

export const getWhatsappConversationsList = async (
  journeyUid: string,
  params?: { page?: number; pageSize?: number; agentUids?: string[] },
): Promise<WhatsappAgentGroupPage> => {
  const apiUrl = brandUrl<WhatsappAgentGroupPage>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/whatsapp-conversations`,
  );
  const response = await fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        journeyUid,
        page: params?.page ?? 1,
        pageSize: params?.pageSize ?? 5,
        ...(params?.agentUids?.length ? { agentUids: params.agentUids } : {}),
      },
    },
  });
  return (response as any).data as WhatsappAgentGroupPage;
};

export interface ChatsAndCallsAgent {
  uid: string;
  name: string | null;
}

export const getChatsAndCallsAgents = async (
  journeyUid: string,
): Promise<ChatsAndCallsAgent[]> => {
  const apiUrl = brandUrl<ChatsAndCallsAgent[]>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/activity/${journeyUid}/chats-and-calls-agents`,
  );
  const response = await fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  });
  return (response as any).data as ChatsAndCallsAgent[];
};

export interface WhatsappConversationByAgentPage {
  messages: WhatsappMessage[];
  nextCursor: string | null;
}

export const getByAgentMessages = async (
  journeyUid: string,
  agentUid: string,
  cursor?: string,
): Promise<WhatsappConversationByAgentPage> => {
  const apiUrl = brandUrl<WhatsappConversationByAgentPage>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/whatsapp-conversations/by-agent/messages`,
  );
  const response = await fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        journeyUid,
        agentUid,
        ...(cursor ? { cursor } : {}),
      },
    },
  });
  return (response as any).data as WhatsappConversationByAgentPage;
};
